import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  crmApi,
  type SegmentKey,
  type LoyaltyAdjustPayload,
  type WhatsAppBroadcastPayload,
  type SMSCampaignPayload,
  type FeedbackListParams,
  type AbandonedCartsListParams,
  type CLVListParams,
  type ReferralsListParams,
  type ReferralConfig,
  type LoyaltyConfig,
  type AddNotePayload,
} from "@/lib/admin-api/crm";

// ---------------------------------------------------------------------------
// Query key constants
// ---------------------------------------------------------------------------

const KEYS = {
  segments: "crm-segments",
  segmentCustomers: (key: SegmentKey) => ["crm-segment-customers", key] as const,
  loyalty: "crm-loyalty",
  loyaltyConfig: "crm-loyalty-config",
  whatsappHistory: "crm-whatsapp-history",
  whatsappTemplates: "crm-whatsapp-templates",
  smsCampaigns: "crm-sms-campaigns",
  smsTemplates: "crm-sms-templates",
  feedback: (params: FeedbackListParams) => ["crm-feedback", params] as const,
  feedbackSummary: "crm-feedback-summary",
  abandonedCarts: (params: AbandonedCartsListParams) => ["crm-abandoned-carts", params] as const,
  abandonedCartsSummary: "crm-abandoned-carts-summary",
  clv: (params: CLVListParams) => ["crm-clv", params] as const,
  referrals: (params: ReferralsListParams) => ["crm-referrals", params] as const,
  referralsConfig: "crm-referrals-config",
  communications: (customerId: string) => ["crm-communications", customerId] as const,
} as const;

// ---------------------------------------------------------------------------
// Segments
// ---------------------------------------------------------------------------

/** Fetch all auto-calculated customer segments, optionally filtered by region. */
export function useSegments(filters?: { region?: string }) {
  const params = new URLSearchParams();
  if (filters?.region) params.set("region", filters.region);
  const qs = params.toString();
  return useQuery({
    queryKey: [KEYS.segments, qs],
    queryFn: () => crmApi.segments.list(qs ? `?${qs}` : undefined),
    staleTime: 5 * 60_000,
  });
}

/** Fetch customers belonging to a specific segment. */
export function useSegmentCustomers(segmentKey: SegmentKey | null) {
  return useQuery({
    queryKey: KEYS.segmentCustomers(segmentKey ?? "vip"),
    queryFn: () => crmApi.segments.customers(segmentKey!),
    enabled: segmentKey != null,
    staleTime: 2 * 60_000,
  });
}

/** Create a new featured (custom) segment. */
export function useCreateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; description?: string; criteria: Record<string, unknown> }) =>
      crmApi.segments.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEYS.segments] });
    },
  });
}

/** Delete a featured (custom) segment by id. */
export function useDeleteSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => crmApi.segments.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEYS.segments] });
    },
  });
}

// ---------------------------------------------------------------------------
// Loyalty
// ---------------------------------------------------------------------------

/** Fetch loyalty dashboard + member list. */
export function useLoyalty() {
  return useQuery({
    queryKey: [KEYS.loyalty],
    queryFn: () => crmApi.loyalty.list(),
    staleTime: 60_000,
  });
}

/** Fetch loyalty programme configuration. */
export function useLoyaltyConfig() {
  return useQuery({
    queryKey: [KEYS.loyaltyConfig],
    queryFn: () => crmApi.loyalty.config.get(),
    staleTime: 10 * 60_000,
  });
}

/** Manually adjust a customer's loyalty points. */
export function useAdjustLoyaltyPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LoyaltyAdjustPayload) => crmApi.loyalty.adjust(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.loyalty] });
    },
  });
}

/** Persist loyalty programme configuration changes. */
export function useUpdateLoyaltyConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: Partial<LoyaltyConfig>) => crmApi.loyalty.config.update(config),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.loyaltyConfig] });
    },
  });
}

// ---------------------------------------------------------------------------
// WhatsApp broadcast
// ---------------------------------------------------------------------------

/** Fetch WhatsApp broadcast history. */
export function useWhatsAppHistory() {
  return useQuery({
    queryKey: [KEYS.whatsappHistory],
    queryFn: () => crmApi.whatsapp.history(),
    staleTime: 60_000,
  });
}

/** Fetch available WhatsApp message templates. */
export function useWhatsAppTemplates() {
  return useQuery({
    queryKey: [KEYS.whatsappTemplates],
    queryFn: () => crmApi.whatsapp.templates.list(),
    staleTime: 10 * 60_000,
  });
}

/** Send or schedule a WhatsApp broadcast. */
export function useWhatsAppBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WhatsAppBroadcastPayload) =>
      crmApi.whatsapp.broadcast(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.whatsappHistory] });
    },
  });
}

// ---------------------------------------------------------------------------
// SMS campaigns
// ---------------------------------------------------------------------------

/** Fetch SMS campaign list. */
export function useSMSCampaigns() {
  return useQuery({
    queryKey: [KEYS.smsCampaigns],
    queryFn: () => crmApi.sms.campaigns.list(),
    staleTime: 60_000,
  });
}

/** Fetch available SMS templates. */
export function useSMSTemplates() {
  return useQuery({
    queryKey: [KEYS.smsTemplates],
    queryFn: () => crmApi.sms.templates.list(),
    staleTime: 10 * 60_000,
  });
}

/** Create a new SMS campaign. */
export function useCreateSMSCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SMSCampaignPayload) =>
      crmApi.sms.campaigns.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.smsCampaigns] });
    },
  });
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

/** Fetch paginated customer feedback list. */
export function useFeedback(params: FeedbackListParams = {}) {
  return useQuery({
    queryKey: KEYS.feedback(params),
    queryFn: () => crmApi.feedback.list(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Fetch aggregated feedback summary (KPIs). */
export function useFeedbackSummary() {
  return useQuery({
    queryKey: [KEYS.feedbackSummary],
    queryFn: () => crmApi.feedback.summary(),
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Abandoned carts
// ---------------------------------------------------------------------------

/** Fetch paginated abandoned cart list. */
export function useAbandonedCarts(params: AbandonedCartsListParams = {}) {
  return useQuery({
    queryKey: KEYS.abandonedCarts(params),
    queryFn: () => crmApi.abandonedCarts.list(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Fetch abandoned cart summary KPIs. */
export function useAbandonedCartsSummary() {
  return useQuery({
    queryKey: [KEYS.abandonedCartsSummary],
    queryFn: () => crmApi.abandonedCarts.summary(),
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Customer Lifetime Value
// ---------------------------------------------------------------------------

/** Fetch paginated CLV table. */
export function useCLV(params: CLVListParams = {}) {
  return useQuery({
    queryKey: KEYS.clv(params),
    queryFn: () => crmApi.clv.list(params),
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
}

// ---------------------------------------------------------------------------
// Referrals
// ---------------------------------------------------------------------------

/** Fetch referral programme summary + sponsor list. */
export function useReferrals(params: ReferralsListParams = {}) {
  return useQuery({
    queryKey: KEYS.referrals(params),
    queryFn: () => crmApi.referrals.list(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Fetch referral programme configuration. */
export function useReferralConfig() {
  return useQuery({
    queryKey: [KEYS.referralsConfig],
    queryFn: () => crmApi.referrals.config.get(),
    staleTime: 10 * 60_000,
  });
}

/** Persist referral programme configuration. */
export function useUpdateReferralConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: Partial<ReferralConfig>) =>
      crmApi.referrals.config.update(config),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.referralsConfig] });
    },
  });
}

// ---------------------------------------------------------------------------
// Customer communications
// ---------------------------------------------------------------------------

/** Fetch full communication timeline for one customer. */
export function useCustomerCommunications(customerId: string | null) {
  return useQuery({
    queryKey: KEYS.communications(customerId ?? ""),
    queryFn: () => crmApi.communications.list(customerId!),
    enabled: customerId != null && customerId !== "",
    staleTime: 30_000,
  });
}

/** Add a manual admin note to a customer's communication log. */
export function useAddCustomerNote(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddNotePayload) =>
      crmApi.communications.addNote(customerId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.communications(customerId) });
    },
  });
}
